// Small display formatters for the Matches tab cards (Hebrew, mock s18 style).
import { fNumber } from "utils/formatNumber";
import type { ProcessedListingData } from "../../../../../../@types/sharedSchema";

/** "רח׳ עזה 12, רחביה" — street + house number, then neighborhood (or city). */
export function buildListingAddressLine(listing: ProcessedListingData): string {
  const address = listing.address ?? ({} as ProcessedListingData["address"]);
  const streetPart = [address?.street, address?.houseNumber].filter(Boolean).join(" ");
  const areaPart = address?.neighborhood || address?.city || "";
  const parts = [streetPart, areaPart].filter(Boolean);
  if (parts.length > 0) return parts.join(", ");
  return address?.city || "כתובת לא ידועה";
}

/** "4 חד׳ · ק״3 + מעלית · 105 מ״ר" */
export function buildListingMetaLine(listing: ProcessedListingData): string {
  const parts: string[] = [];
  if (typeof listing.roomsCount === "number" && listing.roomsCount > 0) {
    parts.push(`${listing.roomsCount} חד׳`);
  }
  const floor = listing.address?.floor;
  if (typeof floor === "number") {
    const floorLabel = floor === 0 ? "ק״ק" : `ק״${floor}`;
    parts.push(listing.hasElevator ? `${floorLabel} + מעלית` : floorLabel);
  }
  if (typeof listing.squareMeter === "number" && listing.squareMeter > 0) {
    parts.push(`${listing.squareMeter} מ״ר`);
  }
  return parts.join(" · ");
}

export function formatPriceFull(price: number | undefined | null): string {
  if (typeof price !== "number" || !isFinite(price) || price <= 0) return "";
  return `₪${fNumber(price)}`;
}

/** Compact chip-style price: ₪2.9M / ₪7.5K. */
export function formatPriceShort(price: number | undefined | null): string {
  if (typeof price !== "number" || !isFinite(price) || price <= 0) return "";
  if (price >= 1_000_000) {
    const millions = price / 1_000_000;
    return `₪${millions.toFixed(millions >= 10 ? 0 : 1)}M`;
  }
  if (price >= 1_000) {
    // One decimal for non-round thousands (mirrors the M branch) — rounding 7,500
    // to "₪8K" would misstate the actual filter bound the search is running with.
    const thousands = price / 1_000;
    return `₪${Number.isInteger(thousands) ? thousands : thousands.toFixed(1)}K`;
  }
  return `₪${fNumber(price)}`;
}

// Hebrew labels for the amenity boolean fields.
//
// RE-EXPORTED, not declared. This map used to be written out here AND in
// OverviewSearchProfilePanel, byte-identically — two copies of thirteen strings that
// several screens render side by side. The one definition now lives in
// utils/contacts/amenityLabels.ts; the name is kept here so every existing import site
// is untouched.
export { AMENITY_LABELS } from "utils/contacts/amenityLabels";

/**
 * Tolerant date coercion. Firestore-sourced listings carry real Dates (deep Timestamp
 * conversion in GenericConverter), but Algolia hits only rehydrate TOP-LEVEL keys whose
 * name contains "time" — nested fields (priceHistory[].date) and *-Date keys
 * (lastPriceChangeDate, entranceDate) arrive as epoch millis. Handle all shapes.
 */
export function coerceDate(value: Date | number | string | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** "18.7.2026" — compact absolute date for history rows / date lines. */
export function formatShortDate(value: Date | number | string | null | undefined): string {
  const d = coerceDate(value);
  if (!d) return "";
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

const twoDigits = (n: number): string => `${n}`.padStart(2, "0");

/** "היום 21:03" / "אתמול 21:03" / "12.7" — the mock's compact activity timestamps. */
export function formatRelativeDayTime(date: Date | undefined | null): string {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 24 * 60 * 60 * 1000);
  const time = `${twoDigits(d.getHours())}:${twoDigits(d.getMinutes())}`;
  if (d >= startOfToday) return `היום ${time}`;
  if (d >= startOfYesterday) return `אתמול ${time}`;
  return `${d.getDate()}.${d.getMonth() + 1}`;
}
